import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Shield,
  Zap,
  Globe,
  Heart,
  Target,
  Users,
  Sparkles,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";

const values = [
  {
    icon: Zap,
    title: "Speed first",
    description:
      "Every feature is measured in seconds saved. If it slows you down, it doesn't ship.",
  },
  {
    icon: Shield,
    title: "Privacy by default",
    description:
      "End-to-end encryption, no data resold, no shadow tracking. Your meetings belong to you.",
  },
  {
    icon: Heart,
    title: "Built for humans",
    description:
      "Sensible defaults, calm interfaces, zero jargon. Software should feel effortless.",
  },
  {
    icon: Globe,
    title: "Open to the world",
    description:
      "Works on any device, any browser, in 150+ countries. No installs. No gatekeepers.",
  },
];

const milestones = [
  { year: "2021", event: "MeetHub founded with a mission to kill meeting friction." },
  { year: "2022", event: "Launched breakout rooms, polls, and live captions." },
  { year: "2023", event: "Reached 500K active users across 150+ countries." },
  { year: "2024", event: "Released AI noise suppression and recording transcripts." },
  { year: "2025", event: "Shipped instant meetings — start a call in one click." },
];

const stats = [
  { value: "500K+", label: "Active users" },
  { value: "10M+", label: "Meetings hosted" },
  { value: "150+", label: "Countries" },
  { value: "99.9%", label: "Uptime SLA" },
];

export default function About() {
  return (
    <div className="marketing-page min-h-screen bg-background">
      <Seo
        title="About MeetHub — The meeting platform built for serious work"
        description="MeetHub is a modern video meeting platform built for speed, privacy, and reliability. Learn about our mission, values, and the team behind the product."
        canonicalPath="/about"
      />
      <Navigation />

      <main className="pt-24">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-soft border-b">
          <div className="absolute inset-0 bg-gradient-hero pointer-events-none" />
          <div className="container relative mx-auto px-4 py-16 sm:py-24">
            <div className="mx-auto max-w-3xl text-center">
              <Badge variant="secondary" className="mb-6 inline-flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" />
                Our story
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
                Meetings, finally{" "}
                <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                  the way they should be
                </span>
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-muted-foreground leading-relaxed">
                We're building the meeting platform we always wanted: instant,
                reliable, private — and respectful of your time.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button size="lg" asChild>
                  <Link to="/register">
                    Start free
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/features">See what's inside</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat) => (
              <Card key={stat.label} className="border-border/60">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-primary">
                    {stat.value}
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Mission */}
        <section className="container mx-auto px-4 py-16 sm:py-20">
          <div className="mx-auto max-w-3xl">
            <Badge variant="outline" className="mb-4">Mission</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Make every meeting worth showing up to.
            </h2>
            <div className="mt-6 space-y-4 text-lg text-muted-foreground leading-relaxed">
              <p>
                Meetings should not require downloads, accounts, plugins, or a
                15-minute setup tutorial. They should just work — for the host,
                the participants, and everyone joining from a phone in a coffee
                shop.
              </p>
              <p>
                We started MeetHub because the existing tools were too slow,
                too bloated, or too expensive for modern teams. So we built a
                lightweight, browser-first platform that opens in one click and
                respects your privacy by default.
              </p>
              <p>
                Today, freelancers, startups, and growing teams across 150+
                countries trust MeetHub for sales calls, client reviews,
                webinars, and internal stand-ups. We're just getting started.
              </p>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="bg-muted/30 border-y border-border/60">
          <div className="container mx-auto px-4 py-16 sm:py-20">
            <div className="mx-auto max-w-2xl text-center mb-12">
              <Badge variant="outline" className="mb-4">What we believe</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
                The principles behind every decision
              </h2>
              <p className="mt-4 text-muted-foreground">
                Our values aren't posters on a wall. They're how we ship.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value) => (
                <Card key={value.title} className="border-border/60 hover:border-primary/30 transition-colors">
                  <CardContent className="p-6">
                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary mb-4">
                      <value.icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{value.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="container mx-auto px-4 py-16 sm:py-20">
          <div className="mx-auto max-w-3xl">
            <div className="text-center mb-12">
              <Badge variant="outline" className="mb-4">Journey</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
                A short history of MeetHub
              </h2>
            </div>
            <div className="relative">
              <div className="absolute left-[27px] top-2 bottom-2 w-px bg-border" aria-hidden />
              <div className="space-y-8">
                {milestones.map((milestone) => (
                  <div key={milestone.year} className="flex gap-6 items-start">
                    <div className="flex-shrink-0 w-14 h-14 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center relative z-10 bg-background">
                      <span className="text-primary font-semibold text-sm">{milestone.year}</span>
                    </div>
                    <div className="flex-1 pt-3">
                      <p className="text-foreground">{milestone.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* What makes us different */}
        <section className="container mx-auto px-4 py-16 sm:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="outline" className="mb-4">Why MeetHub</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
                Built different, on purpose
              </h2>
              <p className="mt-4 text-muted-foreground text-lg leading-relaxed">
                We're not trying to be the biggest meeting platform. We're
                trying to be the one professionals reach for when reliability
                matters.
              </p>
              <ul className="mt-8 space-y-4">
                {[
                  "Browser-first — no downloads, no admin permissions",
                  "End-to-end encryption on every call",
                  "Generous free plan that actually works for real teams",
                  "Real humans in support, not chatbots",
                  "Transparent pricing — no surprise upcharges",
                ].map((item) => (
                  <li key={item} className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Card className="border-border/60 bg-gradient-to-br from-primary/5 to-transparent">
              <CardContent className="p-8 space-y-6">
                <div className="flex items-start gap-4">
                  <Target className="h-8 w-8 text-primary flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">For modern teams</h3>
                    <p className="text-sm text-muted-foreground">
                      Designed for distributed teams, async culture, and the
                      reality of back-to-back calls.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Users className="h-8 w-8 text-primary flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">For freelancers</h3>
                    <p className="text-sm text-muted-foreground">
                      Look professional in client calls without paying enterprise prices.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Sparkles className="h-8 w-8 text-primary flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">For startups</h3>
                    <p className="text-sm text-muted-foreground">
                      Scale from 2 to 200 without changing tools or breaking the bank.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA */}
        <section className="container mx-auto px-4 pb-20">
          <Card className="border-border/60 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground overflow-hidden">
            <CardContent className="p-10 sm:p-14 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
                Start a meeting in one click
              </h2>
              <p className="mt-4 text-lg text-primary-foreground/90 max-w-xl mx-auto">
                Join the teams replacing slow, bloated meeting tools with
                something that actually respects their time.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button size="lg" variant="secondary" asChild>
                  <Link to="/register">
                    Get started free
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
                  asChild
                >
                  <Link to="/contact">Talk to us</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
}
